import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconArrowLeft, IconCaretRight } from '@redteclab/icons'

import { DialogContent, DialogTitle } from 'base-ui'

import { type CategoryHierarchyResponseDto } from '../CategoryHierarchyResponseDto.type'

import { useCategoriesNavigationContext } from './StoryblokCategoryNavigationProvider'

const categoryNavigationAccessorIsRootCategory = (
  category: CategoryHierarchyResponseDto,
): boolean => category.akeneoCode === 'root'

export const StoryblokCategoryNavigationContent: FC<{
  categoryData?: CategoryHierarchyResponseDto | null
}> = ({ categoryData }) => {
  const { handleClose, navigateToCategory } = useCategoriesNavigationContext()

  if (!categoryData) {
    return null
  }

  const isRootCategory = categoryNavigationAccessorIsRootCategory(categoryData)

  return (
    <>
      <DialogTitle
        className="min-h-14 items-center bg-light-brand-high"
        onClose={handleClose}
        slotProps={{ closeButton: { className: 'mr-5' } }}
      >
        {isRootCategory ? (
          <span className="pl-3.5">
            <FormattedMessage id="general.label.categories" />
          </span>
        ) : (
          <span className="flex items-center">
            <button
              className="flex items-center fill-dark-primary-max px-3.5 text-dark-primary-max"
              onClick={() => {
                if (categoryData.parent?.akeneoCode) {
                  navigateToCategory(categoryData.parent.akeneoCode, 'backward')
                }
              }}
              type="button"
            >
              <IconArrowLeft className="size-7" />
              <span className="sr-only">
                <FormattedMessage id="general.link.label.back" />
              </span>
            </button>
            <span className="grow">
              <FormattedMessage id="general.link.label.back" />
            </span>
          </span>
        )}
      </DialogTitle>
      <DialogContent>
        {isRootCategory ? null : (
          <div
            className={clsx(
              'relative flex justify-between px-5 py-3.5 font-medium',
              'after:absolute after:inset-x-5 after:bottom-0 after:h-0 after:border-b after:border-dark-primary-min',
            )}
          >
            {categoryData.name}
          </div>
        )}
        <ul>
          {categoryData.children?.map((category) => {
            return (
              <li
                className={clsx(
                  isRootCategory ? 'px-5' : 'pl-11 pr-5',
                  'relative flex py-3.5',
                  'after:absolute after:inset-x-5 after:bottom-0 after:h-0 after:border-b after:border-dark-primary-min',
                )}
                key={category.akeneoCode}
              >
                <a
                  className="flex grow items-start justify-between gap-x-2.5"
                  href={category.seoLink}
                  onClick={(event) => {
                    if (!category.hasChildren) {
                      return
                    }

                    event.preventDefault()
                    navigateToCategory(category.akeneoCode, 'forward')
                  }}
                >
                  <span className="flex w-full flex-row items-center gap-1">
                    {category.name}
                  </span>
                  {category.hasChildren ? (
                    <IconCaretRight className="size-6 shrink-0 grow text-dark-brand" />
                  ) : null}
                </a>
              </li>
            )
          })}
        </ul>
      </DialogContent>
    </>
  )
}
